import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCheck, ClipboardList, FileText, Save, Send, UserRound } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import { DateInput, Select } from '../../components/Controls';
import { FileChip, FilePicker } from '../../components/Files';
import { LEAVE_ATTACHMENT_RULE } from '../../fileRules';
import { num, typeId, workingDays } from './shared';

// Apply for Leave (Save Draft / Submit Leave) and Add for Employee (manager / admin: Save Draft / Save & Approve)
// — Stephanet's leavemgmt/ApplyForm.jsx

const BLANK = { employee_id: '', leave_type: '', start_date: '', end_date: '', reason: '', reliever_id: '', emergency_contact: '', handover_note: '' };

// saves a request (draft / submit / approve) and uploads its attachment; returns the server's reply
export async function saveRequest(data, f, how, file, notify) {
  const r = await hr.applyLeave({
    employee_id: f.employee_id, leave_type_id: typeId(data, f.leave_type), start_date: f.start_date, end_date: f.end_date, reason: f.reason,
    reliever_id: f.reliever_id || null, emergency_contact: f.emergency_contact || null, handover_note: f.handover_note || null, mode: how,
  });
  if (file) {
    try { await hr.uploadLeaveAttachment(r.id, file); } catch (e) { notify(`Saved, but the attachment failed: ${e.message}`, 'bad'); }
  }
  return r;
}

export default function ApplyForm({ data, reload, mode, go }) {
  const { notify } = useHR();
  const [f, setF] = useState(BLANK);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const add = mode === 'add';
  const emp = data.employees.find(e => String(e.id) === String(f.employee_id));
  const types = useMemo(() => data.types.filter(t => t.active !== false && (!emp || !t.gender || t.gender === 'All' || t.gender === emp.gender)
    && (add || t.emp_apply !== false)), [data, emp, add]);
  const type = data.types.find(t => t.name === f.leave_type);
  const days = workingDays(f.start_date, f.end_date, data.holiday_dates);
  const bal = emp && type?.has_balance ? emp.balances[f.leave_type] : null;
  const short = bal && days > bal.available;
  const sameYear = !f.start_date || Number(f.start_date.slice(0, 4)) === data.year;
  const needFile = type?.attachment === 'Required';
  const bad = (f.end_date && f.start_date && f.end_date < f.start_date) ? 'The To date is before the From date'
    : f.start_date && f.end_date && days === 0 ? 'These dates fall on weekends or public holidays only' : '';
  const ready = emp && f.leave_type && f.start_date && f.end_date && f.reason.trim() && !bad;
  const canApprove = add && data.can.approve;

  const save = async how => {
    if (!ready) return;
    if (how !== 'draft' && needFile && !file) { notify(`${f.leave_type} leave needs an attachment (medical certificate or supporting document)`, 'bad'); return; }
    setBusy(how);
    try {
      const r = await saveRequest(data, f, how, file, notify);
      notify(r.message || r.reference);
      setF(BLANK); setFile(null);
      await reload();
      go(how === 'draft' ? 'manage' : 'requests', how === 'draft' ? { status: 'draft' } : undefined);
    } catch (e) { notify(e.message, 'bad'); }
    setBusy('');
  };

  return (
    <form className="mdr-card lvm-form" onSubmit={e => { e.preventDefault(); save(add && canApprove ? 'approve' : 'submit'); }}>
      <div className="lvm-form-head">
        <div className="mds-card-title">{add ? <UserRound size={17} /> : <ClipboardList size={17} />} {add ? 'Add Leave for Employee' : 'Apply for Leave'}</div>
        <p>{add ? 'Manager/Admin direct leave allocation' : 'Submit a leave request for approval'}</p>
      </div>

      <section>
        <h4><span>A</span> Section A — Employee Details</h4>
        <div className="lvm-grid">
          <label className="span2">Employee ID *
            <Select value={f.employee_id} onChange={e => set('employee_id', e.target.value)} required>
              <option value="">Select employee…</option>
              {data.employees.map(e => <option key={e.id} value={e.id}>{`${e.employee_no} — ${e.name}${e.status && !['active', 'probation'].includes(e.status) ? ` (${e.status})` : ''}`}</option>)}
            </Select>
          </label>
          <label>Employee Name<input value={emp?.name || ''} readOnly placeholder="—" /></label>
          <label>Email<input value={emp?.email || ''} readOnly placeholder="—" /></label>
          <label>Department<input value={emp?.department || ''} readOnly placeholder="—" /></label>
          <label>Grade<input value={emp?.grade || ''} readOnly placeholder="—" /></label>
          <label>Project Site<input value={emp?.site || ''} readOnly placeholder="—" /></label>
          <label>Reporting Manager<input value={emp?.manager || ''} readOnly placeholder="—" /></label>
        </div>
      </section>

      <section>
        <h4><span>B</span> Section B — Leave Details</h4>
        <div className="lvm-grid">
          <label>Leave Type *
            <Select value={f.leave_type} onChange={e => set('leave_type', e.target.value)} required>
              <option value="">Select leave type</option>
              {types.map(t => <option key={t.id} value={t.name}>{`${t.name}${emp && t.has_balance && emp.balances[t.name] ? ` (${num(emp.balances[t.name].available)} available)` : ''}`}</option>)}
            </Select>
          </label>
          <label>From Date *<DateInput value={f.start_date} onChange={e => set('start_date', e.target.value)} required /></label>
          <label>To Date *<DateInput value={f.end_date} onChange={e => set('end_date', e.target.value)} required /></label>
          <label className="span3">Reason for Leave *<textarea rows={3} value={f.reason} onChange={e => set('reason', e.target.value)} required placeholder="Reason for the leave…" /></label>
        </div>
        {(f.start_date || f.leave_type) && (
          <div className="lvm-calc">
            <div><small>Working Days</small><b>{days || '—'}</b><span>weekends & holidays excluded</span></div>
            <div><small>Payment</small><b>{type ? (type.paid ? 'Paid' : 'Unpaid') : '—'}</b><span>{type?.paid === false ? 'salary deduction applies' : 'no deduction'}</span></div>
            <div className={short ? 'bad' : ''}><small>Balance {sameYear ? '' : `(${data.year})`}</small><b>{bal ? num(bal.available) : type && !type.has_balance ? 'Not tracked' : '—'}</b>
              <span>{bal ? `after this: ${num(bal.available - days)}` : ' '}</span></div>
            <div><small>Attachment</small><b>{type ? type.attachment : '—'}</b><span>{needFile ? 'certificate needed' : 'optional'}</span></div>
          </div>
        )}
        {bad && <div className="mdl-warn"><AlertTriangle size={13} /> {bad}</div>}
        {short && <div className="mdl-warn"><AlertTriangle size={13} /> {emp.name} has {num(bal.available)} day(s) of {f.leave_type} leave available — this request needs {days}. It will be refused on submit.</div>}
      </section>

      <section>
        <h4><span>C</span> Section C — Additional Information</h4>
        <div className="lvm-grid">
          <label>Reliever/Replacement Employee
            <Select value={f.reliever_id} onChange={e => set('reliever_id', e.target.value)}>
              <option value="">None</option>
              {data.employees.filter(e => String(e.id) !== String(f.employee_id)).map(e => <option key={e.id} value={e.id}>{`${e.employee_no} — ${e.name}`}</option>)}
            </Select>
          </label>
          <label>Emergency Contact During Leave<input value={f.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} placeholder="Name and phone" /></label>
          <label className="span3">Handover Note<textarea rows={2} value={f.handover_note} onChange={e => set('handover_note', e.target.value)} placeholder="Work handed over, pending tasks…" /></label>
          <div className="span3 lvm-attach">
            <span className="lvm-label">Attachment (Medical cert, supporting doc){needFile ? ' *' : ''}</span>
            {file ? <FileChip name={file.name} size={file.size} pending onRemove={() => setFile(null)} />
              : <FilePicker rule={LEAVE_ATTACHMENT_RULE} onPick={setFile} label="Click to upload or drag file here" onError={m => notify(m, 'bad')} />}
          </div>
        </div>
      </section>

      <div className="mdh-form-foot lvm-form-foot">
        <button type="button" className="mdr-btn" disabled={!ready || !!busy} onClick={() => save('draft')}><Save size={14} /> {busy === 'draft' ? 'Saving…' : 'Save Draft'}</button>
        {add
          ? <button type="submit" className="mds-run lvm-ok" disabled={!ready || !!busy || !canApprove} title={canApprove ? '' : 'You need approve rights'}><CheckCheck size={14} /> {busy === 'approve' ? 'Saving…' : 'Save & Approve'}</button>
          : <button type="submit" className="mds-run" disabled={!ready || !!busy}><Send size={14} /> {busy === 'submit' ? 'Submitting…' : 'Submit Leave'}</button>}
      </div>
      {!add && <p className="lvm-note"><FileText size={13} /> Submitted requests go to the supervisor, then HR for final approval. Approved days come off the balance and are marked on the attendance register.</p>}
    </form>
  );
}
